import Typography from "@mui/material/Typography";
import { MODULE_TYPE_LABELS } from "entities/modules/constants";
import {
  PageModuleDataWithPageId,
  PageModuleStatus,
  Page,
} from "entities/pages/types";
import { chipColorsMap, chipTextMap } from "modules/brands/utils";
import {
  StyledCell,
  StyledRow,
  StyledNameCell,
} from "shared/components/CollapsibleTable/styled";
import CustomTooltip from "shared/components/CustomTooltip";
import DropdownList from "shared/components/DropdownList";
import FlexCenter from "shared/components/Layout/components/FlexCenter";
import StatusBadge, {
  StatusBadgeBaseProps,
} from "shared/components/StatusBadge";
import { StarsIcon } from "shared/theme/icons/StarsIcon";
import { formatScheduledDate } from "shared/utils/formatScheduledDate";

export type Module = PageModuleDataWithPageId & {
  isEmpty?: boolean;
  message?: string;
};

type ModuleStatusItem = StatusBadgeBaseProps & {
  pageId: string;
};

type ModuleStatusParams = {
  pageId: string;
};

const ModuleStatusBadge = ({
  module,
  handleStatus,
  readOnly,
}: Readonly<{
  module: PageModuleDataWithPageId;
  handleStatus?: (params: {
    moduleId: string;
    status: PageModuleStatus;
    pageId: string;
  }) => void;
  readOnly?: boolean;
}>) => {
  const statusItem: ModuleStatusItem = {
    id: module.id,
    status: module.status,
    title: chipTextMap[module.status],
    color: chipColorsMap[module.status],
    pageId: module.pageId,
    readOnly,
  };

  const handleStatusChange = (
    params: {
      id: string;
      status: PageModuleStatus;
    } & ModuleStatusParams,
  ) => {
    if (handleStatus) {
      handleStatus({
        moduleId: params.id,
        status: params.status,
        pageId: params.pageId,
      });
    }
  };

  return (
    <StatusBadge<ModuleStatusItem, ModuleStatusParams>
      item={statusItem}
      handleStatus={handleStatusChange}
      readOnly={readOnly}
      additionalParams={{
        pageId: module.pageId,
      }}
    />
  );
};

export const scheduleColumns = [
  { key: "brandId", title: "Brand", width: "10%" },
  { key: "pageId", title: "Page", width: "10%" },
  { key: "event", title: "Event", width: "15%" },
  { key: "scheduledStartAt", title: "Start", width: "10%" },
  { key: "scheduledEndAt", title: "End", width: "10%" },
  { key: "type", title: "Content Type", width: "10%" },
  { key: "name", title: "Title", width: "15%" },
  { key: "status", title: "Status", width: "10%" },
  { key: "actions", title: "Actions", width: "10%" },
];

export const renderModuleRow = (
  module: Module,
  moduleDetailsDrawer: any,
  onUpdateModuleStatus?: (params: {
    moduleId: string;
    status: PageModuleStatus;
    pageId: string;
  }) => void,
  pageList?: Page[],
) => {
  if (module.isEmpty) {
    return (
      <StyledRow key={module.id ?? "empty"} style={{ maxHeight: "48px" }}>
        <StyledCell
          colSpan={scheduleColumns.length}
          width="100%"
          style={{ textAlign: "left" }}
        >
          <Typography variant="body2" color="textSecondary">
            {module.message}
          </Typography>
        </StyledCell>
      </StyledRow>
    );
  }

  const actionItems = [
    {
      title: "Edit",
      onClick: () => moduleDetailsDrawer.openDrawer(module),
    },
  ];

  const pageName = pageList?.find((page) => page.id === module.pageId)?.title;

  return (
    <StyledRow key={module.id} style={{ maxHeight: "48px" }}>
      <StyledCell width={scheduleColumns[0].width}>{module.brandId}</StyledCell>
      <StyledCell width={scheduleColumns[1].width}>{pageName}</StyledCell>
      <StyledCell width={scheduleColumns[2].width}>
        <CustomTooltip title={module.relatedEvent?.name} width="100%">
          <StyledNameCell>{module.relatedEvent?.name ?? "-"}</StyledNameCell>
        </CustomTooltip>
      </StyledCell>
      <StyledCell width={scheduleColumns[3].width}>
        {formatScheduledDate(module.scheduledStartAt)}
      </StyledCell>
      <StyledCell width={scheduleColumns[4].width}>
        {formatScheduledDate(module.scheduledEndAt)}
      </StyledCell>
      <StyledCell width={scheduleColumns[5].width}>
        <FlexCenter gap="0.5rem">
          {MODULE_TYPE_LABELS[module.type]}{" "}
          {module.isAiGenerated ? <StarsIcon variant="colored" /> : null}
        </FlexCenter>
      </StyledCell>
      <StyledCell width={scheduleColumns[6].width}>
        <CustomTooltip title={module.name} width="100%">
          <StyledNameCell>{module.name}</StyledNameCell>
        </CustomTooltip>
      </StyledCell>
      <StyledCell width={scheduleColumns[7].width}>
        <ModuleStatusBadge
          module={module}
          handleStatus={onUpdateModuleStatus}
        />
      </StyledCell>
      <StyledCell width={scheduleColumns[8].width} align="right">
        <DropdownList items={actionItems} />
      </StyledCell>
    </StyledRow>
  );
};
